import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Input } from 'reactstrap';
import Select, { components } from 'react-select';
import Icon from '@mdi/react';
import { mdiAlertCircleOutline } from '@mdi/js';
import useExpenseTypes from '../../hooks/useExpenseTypes';
import ExpenseDownloadAttachmentIcon from '../../../assets/icons/expense-download-attachment.svg';
import ExpenseAddMoreIcon from '../../../assets/icons/expense-add-more.svg';
import { useProcessExpenseClaimCtx } from '../../../pages/ExpenseClaims/HR/context/ProcessExpenseClaimProvider';
import { useExpenseClaimUtilCtx } from '../../../pages/ExpenseClaims/context/ExpenseClaimUtilProvider';
import ProcessClaimAdditionalDetailsModal from '../ProcessClaimAdditionalDetailsModal/ProcessClaimAdditionalDetailsModal';
import { FileUtil } from '../../../utils';
import {
  CLAIM_FORM_TYPES,
  getClaimStatusColor,
} from '../../../pages/ExpenseClaims/Employee/EmployeeExpenseClaim/constants';
import { mainFieldKeys } from './constants';
import { DatePicker } from '../../Common';
import AmountInputProps from '../AmountInputProps/AmountInputProps';
// Import SCSS
import './ProcessClaimRow.scss';

// Replace chevron with error icon
function ErrorIndicator(props) {
  return (
    <components.DropdownIndicator {...props}>
      <Icon path={mdiAlertCircleOutline} size={0.75} color="red" />
    </components.DropdownIndicator>
  );
}

function ProcessClaimRow({
  index,
  item,
  currencyLists,
  setSearchText,
  methods,
  watch,
  formType = null,
  accdList,
}) {
  const {
    formMethods,
    resetRowModalState,
    expenseClaimHeaders,
    setViewAttachmentsModalOpen,
    setViewAttachmentSrNo,
    setExpenseTypeMapping,
    expenseTypeMapping,
    validateLastRow,
    addRow,
    defaultExpenseData,
  } = useProcessExpenseClaimCtx();
  const {
    fetchAttachmentsMutation,
    downloadAttachmentMutation,
    defaultCurrCode,
    expenseCategories,
    reimbursateRateMutation,
  } = useExpenseClaimUtilCtx();
  const { control } = useFormContext();
  const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState(false);
  const rowWatchValues = formMethods.watch(`claims.${index}`);
  const toggleModal = () => {
    setIsAdditionalModalOpen(prev => !prev);
  };

  const { data: rowExpenseTypes = [] } = useExpenseTypes(
    rowWatchValues?.expCategory,
  );

  useEffect(() => {
    if (rowExpenseTypes?.length > 0 && expenseTypeMapping) {
      const newExpenseTypeMapping = rowExpenseTypes.reduce(
        (acc, type) => {
          if (acc[type?.Code]) return acc;
          acc[type?.Code] = type?.Description;
          return acc;
        },
        { ...expenseTypeMapping },
      );
      setExpenseTypeMapping(prev => ({
        ...prev,
        ...newExpenseTypeMapping,
      }));
    }
  }, [rowExpenseTypes, setExpenseTypeMapping]);

  const sortedCurrencies = [...(currencyLists || [])].sort((a, b) => {
    const aIsDefault = a.Code?.trim() === defaultCurrCode?.trim();
    const bIsDefault = b.Code?.trim() === defaultCurrCode?.trim();
    if (aIsDefault) return -1;
    if (bIsDefault) return 1;
    return 0;
  });

  const currencyOptions = sortedCurrencies.map(option => ({
    value: option.Code,
    label: `${option.Name} : ${option.Code}`,
  }));

  useEffect(() => {
    if (
      !rowWatchValues?.currency ||
      !rowWatchValues?.expDate ||
      !expenseClaimHeaders?.finCurr ||
      !expenseClaimHeaders?.claimCurr ||
      !defaultExpenseData?.[0]?.CurrCd?.trim()
    ) {
      return;
    }
    if (rowWatchValues.currency && !rowWatchValues.expRate) {
      (async () => {
        try {
          const response = await reimbursateRateMutation.mutateAsync({
            fromCurr: rowWatchValues.currency,
            toCurr: expenseClaimHeaders?.claimCurr,
            expenseDate: rowWatchValues.expDate,
          });

          const baseResponse = await reimbursateRateMutation.mutateAsync({
            fromCurr: rowWatchValues.currency,
            toCurr: expenseClaimHeaders?.finCurr?.trim(),
            expenseDate: rowWatchValues.expDate,
          });

          let rate = null;
          let baseRate = null;
          if (response?.[0]?.Rate) {
            rate = parseFloat(response?.[0]?.Rate).toFixed(6);
          } else {
            rate = parseFloat(1).toFixed(6);
          }

          if (baseResponse?.[0]?.Rate) {
            baseRate = parseFloat(baseResponse?.[0]?.Rate).toFixed(6);
          } else {
            baseRate = parseFloat(1).toFixed(6);
          }

          methods.setValue(`claims.${index}.expRate`, rate);
          methods.setValue(`claims.${index}.baseRate`, baseRate);
          // Trigger validation for expRate and baseRate
          methods.trigger([
            `claims.${index}.expRate`,
            `claims.${index}.baseRate`,
          ]);
        } catch (err) {
          /* empty */
        }
      })();
    }
  }, [
    rowWatchValues?.currency,
    rowWatchValues?.expDate,
    expenseClaimHeaders?.claimCurr,
    expenseClaimHeaders?.finCurr,
    defaultExpenseData?.[0]?.CurrCd?.trim(),
  ]);

  const handleFileDownload = async () => {
    if (!rowWatchValues?.srNo) {
      toast.error('No file to download. Please upload a file first.', {
        className: 'toast-error',
      });
      return;
    }
    if (!expenseClaimHeaders?.claimNo) {
      toast.error(
        'Claim number is not available. Please save the claim first.',
        {
          className: 'toast-error',
        },
      );
      return;
    }
    const rowAttachments = await fetchAttachmentsMutation.mutateAsync({
      docType: 'DOC_TYPE_EXPENSE_CLAIM',
      claimNo: expenseClaimHeaders?.claimNo || '',
      docIndex: rowWatchValues?.srNo,
      finCoCd: expenseClaimHeaders?.finCoCd || '',
    });

    if (!rowAttachments || rowAttachments.length === 0) {
      toast.error('No attachments found for this row.', {
        className: 'toast-error',
      });
      return;
    }

    if (rowAttachments.length === 1) {
      try {
        const attachmentBlob = await downloadAttachmentMutation.mutateAsync({
          type: 'Expense_Claim',
          fileName: rowAttachments?.[0]?.DocName,
          claimNo: expenseClaimHeaders?.claimNo || '',
          finCoCd: expenseClaimHeaders?.finCoCd || '',
        });
        FileUtil.downloadBlobFile(
          attachmentBlob,
          rowAttachments?.[0]?.DocName || 'attachment',
        );
        return;
      } catch (error) {
        return;
      }
    }
    setViewAttachmentsModalOpen(true);
    setViewAttachmentSrNo(rowWatchValues?.srNo);
  };

  const getClaimRowStyle = () => {
    if (
      expenseClaimHeaders?.claimStatus &&
      formType === CLAIM_FORM_TYPES.VIEW
    ) {
      return {
        backgroundColor: getClaimStatusColor(expenseClaimHeaders?.claimStatus),
      };
    }
    return {};
  };

  const {
    formState: { errors },
  } = formMethods;

  const checkErrorOpenModal = rowErrs => {
    if (!rowErrs) return;
    const mainErrors = mainFieldKeys.some(key => Boolean(rowErrs[key]));
    const additionalErrors = Object.keys(rowErrs)
      .filter(key => !mainFieldKeys.includes(key))
      .some(key => Boolean(rowErrs[key]));
    if (!mainErrors && additionalErrors && !isAdditionalModalOpen) {
      toggleModal();
    }
  };

  const checkLastRowAndAdd = async () => {
    const lastRowIndex = methods.getValues('claims').length - 1;
    if (index === lastRowIndex) {
      const testRes = await validateLastRow(false, false);
      if (!testRes?.isValid) {
        checkErrorOpenModal(testRes?.errors);
        return;
      }
      addRow();
    }
  };

  const setPayAndBaseAmount = amount => {
    const expRate = parseFloat(rowWatchValues?.expRate) || 0;
    const baseRate = parseFloat(rowWatchValues?.baseRate) || 0;
    const reimburAmt = amount * expRate;
    const baseAmt = amount * baseRate;
    methods.setValue(`claims.${index}.reimburAmt`, reimburAmt.toFixed(2));
    methods.setValue(`claims.${index}.baseAmt`, baseAmt.toFixed(2));
    methods.trigger([`claims.${index}.reimburAmt`, `claims.${index}.baseAmt`]);
  };

  const setNewAmount = rate => {
    const amount = parseFloat(rowWatchValues?.amount) || 0;
    const reimburAmt = amount * rate;
    methods.setValue(`claims.${index}.reimburAmt`, reimburAmt.toFixed(2));
  };

  const setNewBaseAmount = rate => {
    const amount = parseFloat(rowWatchValues?.amount) || 0;
    const baseAmt = amount * rate;
    methods.setValue(`claims.${index}.baseAmt`, baseAmt.toFixed(2));
  };

  async function updateRowRates(newCurr) {
    if (
      !newCurr ||
      !rowWatchValues?.expDate ||
      !expenseClaimHeaders?.claimCurr ||
      !defaultCurrCode
    ) {
      return;
    }

    try {
      const response = await reimbursateRateMutation.mutateAsync({
        fromCurr: newCurr,
        toCurr: expenseClaimHeaders?.claimCurr,
        expenseDate: rowWatchValues.expDate,
      });

      const baseResponse = await reimbursateRateMutation.mutateAsync({
        fromCurr: newCurr,
        toCurr: expenseClaimHeaders?.finCurr,
        expenseDate: rowWatchValues.expDate,
      });

      let rate = null;
      let baseRate = null;
      if (response?.[0]?.Rate) {
        rate = parseFloat(response?.[0]?.Rate).toFixed(6);
      } else {
        rate = parseFloat(1).toFixed(6);
      }

      if (baseResponse?.[0]?.Rate) {
        baseRate = parseFloat(baseResponse?.[0]?.Rate).toFixed(6);
      } else {
        baseRate = parseFloat(1).toFixed(6);
      }

      methods.setValue(`claims.${index}.expRate`, rate);
      methods.setValue(`claims.${index}.baseRate`, baseRate);
      // Trigger validation for expRate and baseRate
      methods.trigger([`claims.${index}.expRate`, `claims.${index}.baseRate`]);

      // Set NewAmount and base Amount
      setNewAmount(rate);
      setNewBaseAmount(baseRate);
    } catch (err) {
      /* empty */
    }
  }

  return (
    <>
      <tr
        className="process-claim-row"
        key={item.id}
        style={{ ...getClaimRowStyle(), borderBottom: '1px solid #98A2B3' }}
      >
        <td className="p-0 cell-border category-col">
          <Controller
            control={control}
            name={`claims.${index}.expCategory`}
            render={({ field }) => {
              const hasError = Boolean(errors.claims?.[index]?.expCategory);
              return (
                <Input
                  {...field}
                  type="select"
                  className={`eec-input claim-row-input process-claim-select-input ${hasError ? 'is-invalid' : ''}`}
                  onChange={e => {
                    field.onChange(e);
                    methods.setValue(`claims.${index}.expType`, '');
                    resetRowModalState(index);
                  }}
                  disabled={formType === CLAIM_FORM_TYPES.VIEW}
                  title={
                    !field.value
                      ? 'Select the main category of the expense'
                      : expenseCategories?.find(
                          category => category.Code === field.value,
                        )?.Description || ''
                  }
                >
                  <option value="">Category</option>
                  {expenseCategories?.map(option => (
                    <option key={option?.Code} value={option?.Code}>
                      {option?.Description}
                    </option>
                  ))}
                </Input>
              );
            }}
          />
        </td>
        <td className="p-0 cell-border type-col">
          <Controller
            control={control}
            name={`claims.${index}.expType`}
            render={({ field }) => {
              // pull the errors object from your context

              const hasError = Boolean(errors.claims?.[index]?.expType);
              return (
                <Input
                  {...field}
                  innerRef={field.ref}
                  type="select"
                  className={`eec-input claim-row-input process-claim-select-input ${hasError ? 'is-invalid' : ''}`}
                  onChange={e => {
                    field.onChange(e.target.value);
                    resetRowModalState(index);
                  }}
                  disabled={formType === CLAIM_FORM_TYPES.VIEW}
                  title={
                    !field.value
                      ? 'Select the specific type of expense under the chosen category.'
                      : rowExpenseTypes.find(type => type.Code === field.value)
                          ?.Description || ''
                  }
                >
                  <option value="">Type</option>
                  {rowExpenseTypes.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
              );
            }}
          />
        </td>
        <td className="p-0 cell-border billdate-col">
          <Controller
            control={control}
            name={`claims.${index}.expDate`}
            render={({ field }) => {
              const hasError = Boolean(errors.claims?.[index]?.expDate);
              return (
                <DatePicker
                  {...field}
                  disabled={formType === CLAIM_FORM_TYPES.VIEW}
                  className={`eec-input claim-row-input pe-0 ${hasError ? 'is-invalid' : ''}`}
                  error={!!errors?.claims?.[index]?.expDate}
                  showIcon={false}
                  calendarFocus={false}
                />
              );
            }}
          />
        </td>
        <td className="p-0 cell-border">
          <Controller
            control={control}
            name={`claims.${index}.currency`}
            render={({ field }) => (
              <div className="select-with-error">
                <Select
                  {...field}
                  options={currencyOptions}
                  isDisabled={formType === CLAIM_FORM_TYPES.VIEW}
                  classNamePrefix="rs"
                  className="react-select-no-border"
                  value={{
                    label: rowWatchValues?.currency,
                    value: rowWatchValues?.currency,
                  }}
                  onChange={selected => {
                    field.onChange(selected?.value);
                    updateRowRates(selected?.value);
                  }}
                  onInputChange={inputValue => {
                    setSearchText(inputValue);
                    return inputValue;
                  }}
                  isSearchable
                  styles={{
                    control: (base, { isDisabled }) => ({
                      ...base,
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      cursor: isDisabled ? 'not-allowed' : base.cursor,
                      '&:hover': {
                        backgroundColor: isDisabled
                          ? 'transparent'
                          : base.backgroundColor,
                      },
                      width: '100%',
                    }),
                    singleValue: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    placeholder: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 9999,
                      width: '180px',
                      minWidth: '180px',
                    }),
                    valueContainer: base => ({
                      ...base,
                      cursor: 'text',
                    }),
                    dropdownIndicator: () => ({ display: 'none' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  menuPosition="fixed"
                  noOptionsMessage={() => null}
                />
                {errors?.claims?.[index]?.currency && (
                  <div className="select-with-error__icon">
                    <Icon path={mdiAlertCircleOutline} size={0.75} />
                  </div>
                )}
              </div>
            )}
          />
        </td>
        <td className="p-0 cell-border amount-col">
          <Controller
            control={control}
            name={`claims.${index}.amount`}
            render={({ field }) => (
              <AmountInputProps
                value={field.value}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                error={!!errors?.claims?.[index]?.amount}
                onChange={e => {
                  field.onChange(e);
                  setPayAndBaseAmount(e);
                }}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border tax-amount-col">
          <Controller
            control={control}
            name={`claims.${index}.taxAmount`}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter"
                type="number"
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                className={`eec-input claim-row-input ${errors.claims?.[index]?.taxAmount ? 'is-invalid' : ''}`}
              />
            )}
          />
        </td>
        <td className="p-0 text-center cell-border rec-col">
          <Controller
            control={control}
            name={`claims.${index}.exptaxRecover`}
            render={({ field }) => (
              <Input
                {...field}
                className="claim-row-input eec-blue-checkbox"
                type="checkbox"
                checked={field.value === 'Y'}
                onChange={e => field.onChange(e.target.checked ? 'Y' : 'N')}
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border amount-col">
          <Controller
            control={control}
            name={`claims.${index}.expRate`}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                value={field.value ?? ''}
                onChange={e => {
                  // let user type raw value
                  field.onChange(e.target.value);
                  setNewAmount(e.target.value);
                }}
                onBlur={e => {
                  // format only on blur
                  if (e.target.value !== '') {
                    const formatted = parseFloat(e.target.value).toFixed(6);
                    field.onChange(formatted);
                  }
                }}
                disabled={
                  rowWatchValues?.currency?.trim() ===
                  expenseClaimHeaders?.claimCurr?.trim()
                }
                className={`eec-input-rate ${
                  errors.claims?.[index]?.expRate ? 'is-invalid' : ''
                }`}
                style={{
                  color:
                    rowWatchValues?.currency?.trim() ===
                    expenseClaimHeaders?.claimCurr?.trim()
                      ? '#6c757d'
                      : '#000',
                }}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border amount-col">
          <Controller
            control={control}
            name={`claims.${index}.reimburAmt`}
            render={({ field }) => {
              const rawValue = field.value ?? 0;

              // Force commas for testing
              const displayValue = Number(rawValue).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });

              return (
                <Input
                  {...field}
                  value={displayValue}
                  onChange={e =>
                    field.onChange(e.target.value.replace(/,/g, ''))
                  }
                  className={`eec-input claim-row-input ${
                    errors?.claims?.[index]?.reimburAmt ? 'is-invalid' : ''
                  }`}
                  placeholder="0.0"
                  type="text" // text so commas can show
                  disabled
                />
              );
            }}
          />
        </td>
        <td className="p-0 cell-border remarks-col">
          <Controller
            control={control}
            name={`claims.${index}.remarks`}
            render={({ field }) => (
              <Input
                {...field}
                className="eec-input claim-row-input"
                placeholder="Enter Remarks"
                disabled={formType === CLAIM_FORM_TYPES.VIEW}
                onBlur={async e => {
                  if (e.target.value) {
                    checkLastRowAndAdd();
                    if (!rowWatchValues?.acctCode) {
                      formMethods.setFocus(`claims.${index}.acctCode`);
                    }
                  }
                }}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border amount-col">
          <Controller
            control={control}
            name={`claims.${index}.acctCode`}
            render={({ field }) => (
              <div className="select-with-error">
                <Select
                  {...field}
                  options={accdList?.map(option => ({
                    value: option?.GL53001,
                    label: option?.GL53002,
                  }))}
                  isDisabled={formType === CLAIM_FORM_TYPES.VIEW}
                  classNamePrefix="rs"
                  className="react-select-no-border"
                  value={
                    rowWatchValues?.acctCode
                      ? {
                          label: rowWatchValues?.acctCode,
                          value: rowWatchValues?.acctCode,
                        }
                      : null
                  }
                  onChange={selected => field.onChange(selected?.value)}
                  onInputChange={inputValue => {
                    setSearchText(inputValue);
                    return inputValue;
                  }}
                  isSearchable
                  styles={{
                    control: (base, { isDisabled, isFocused }) => ({
                      ...base,
                      backgroundColor: isDisabled
                        ? 'transparent'
                        : 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      cursor: isDisabled ? 'not-allowed' : base.cursor,
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : isFocused,
                      },
                    }),
                    singleValue: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    valueContainer: base => ({
                      ...base,
                      paddingRight: 0,
                    }),
                    placeholder: (base, { isDisabled }) => ({
                      ...base,
                      color: isDisabled ? '#6c757d' : base.color,
                    }),
                    menuPortal: base => ({
                      ...base,
                      zIndex: 9999,
                      width: '180px',
                      minWidth: '180px',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    indicatorsContainer: base => ({
                      ...base,
                      paddingLeft: '1px',
                      paddingRight: '1px',
                    }),
                    dropdownIndicator: base => ({
                      ...base,
                      paddingLeft: '1px',
                      paddingRight: '1px',
                    }),
                  }}
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  menuPosition="fixed"
                  onBlur={async () => {
                    checkLastRowAndAdd();
                  }}
                  placeholder="Select Code"
                  inputId={`claims.${index}.acctCode`}
                  components={
                    errors?.claims?.[index]?.acctCode
                      ? {
                          DropdownIndicator: ErrorIndicator,
                          IndicatorSeparator: null,
                        }
                      : { IndicatorSeparator: null }
                  }
                />
              </div>
            )}
          />
        </td>
        <td className="p-0 cell-border baserate-col">
          <Controller
            control={control}
            name={`claims.${index}.baseRate`}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                value={field.value || ''}
                onBlur={e => {
                  if (e.target.value !== '') {
                    const formatted = parseFloat(e.target.value).toFixed(6);
                    field.onChange(formatted);
                  }
                }}
                className={`eec-input  ${errors.claims?.[index]?.baseRate ? 'is-invalid' : ''}`}
                onChange={e => {
                  field.onChange(e.target.value);
                  setNewBaseAmount(e.target.value);
                }}
              />
            )}
          />
        </td>
        <td className="p-0 cell-border baseamt-col">
          <Controller
            control={control}
            name={`claims.${index}.baseAmt`}
            render={({ field }) => {
              const rawValue = field.value ?? 0;

              // Force commas for testing
              const displayValue = Number(rawValue).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });

              return (
                <Input
                  {...field}
                  value={displayValue}
                  onChange={e =>
                    field.onChange(e.target.value.replace(/,/g, ''))
                  }
                  className={`eec-input claim-row-input ${
                    errors?.claims?.[index]?.baseAmt ? 'is-invalid' : ''
                  }`}
                  placeholder="0.0"
                  type="text" // text so commas can show
                  disabled
                />
              );
            }}
          />
        </td>
        <td className="align-middle text-center cell-border actions-col">
          <div className="d-flex justify-content-center align-items-center gap-1">
            <button
              type="button"
              className="btn btn-link p-0"
              title="Additional Item"
              onClick={handleFileDownload}
            >
              <img
                src={ExpenseDownloadAttachmentIcon}
                alt="Additional Item"
                className="eec-row-icon"
                style={{
                  cursor:
                    rowWatchValues?.count && rowWatchValues?.count > 0
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    rowWatchValues?.count && rowWatchValues?.count > 0
                      ? 1
                      : 0.5,
                }}
              />
            </button>
            <button
              type="button"
              className="btn btn-link p-0"
              title="Additional Item"
              onClick={() => {
                if (!rowWatchValues?.expType) {
                  toast.error(
                    'Please fill in category and types before adding additional items.',
                    {
                      className: 'toast-error',
                    },
                  );
                  return;
                }
                toggleModal();
              }}
            >
              <img
                src={ExpenseAddMoreIcon}
                alt="Additional Item"
                className="eec-row-icon"
              />
            </button>
          </div>
        </td>
      </tr>
      {isAdditionalModalOpen && (
        <ProcessClaimAdditionalDetailsModal
          isOpen={isAdditionalModalOpen}
          toggle={toggleModal}
          modelType={watch(`claims.${index}.expType`)}
          rowData={watch(`claims.${index}`)}
          onSubmit={null}
          rowIndex={index}
          formType={formType}
          expenseClaimHeaders={expenseClaimHeaders}
          checkLastRowAndAdd={checkLastRowAndAdd}
        />
      )}
    </>
  );
}

export default ProcessClaimRow;
